import React, {
  ChangeEvent, useEffect, useRef, useState,
} from 'react';
import { ButtonAction } from '../../../../../../dorian-shared/types/event/Event';
import { api } from '../../../../../api';
import { TextField } from '../../../../../ui/_fields/TextField/TextField';

interface ButtonTargetInputProps {
    error?: string
    buttonAction: ButtonAction,
    value: string,
    onChange: (value: string) => void,
}

type BookLookupResponse = { success: boolean; book: { title: string } | null };

function StoryBookUuidField(props: {
  value: string
  onChange: (value: string) => void
  error?: string
}) {
  const { value, onChange, error } = props;
  let storyUuid = '';
  try {
    storyUuid = JSON.parse(value).story_uuid;
  } catch {
    // TODO: report to sentry
  }

  const [matchedBookTitle, setMatchedBookTitle] = useState<string | null>(null);
  const lookupSeq = useRef(0);

  useEffect(() => {
    if (storyUuid.length !== 36) {
      setMatchedBookTitle(null);
      return;
    }
    const seq = ++lookupSeq.current;
    const handle = window.setTimeout(() => {
      api
        .get<BookLookupResponse>(`/v1/books/by-uuid/${encodeURIComponent(storyUuid)}`)
        .then((res) => {
          if (lookupSeq.current !== seq) return;
          const book = res.data?.book;
          setMatchedBookTitle(book?.title ?? null);
        })
        .catch(() => {
          if (lookupSeq.current !== seq) return;
          setMatchedBookTitle(null);
        });
    }, 300);
    return () => {
      window.clearTimeout(handle);
    };
  }, [storyUuid]);

  const handleChange = (event: ChangeEvent<HTMLInputElement>) => {
    const newValue = JSON.stringify({
      story_uuid: event.target.value,
    });
    onChange(newValue);
  };

  return (
    <>
      <TextField
        label="Story UUID"
        name="storyUuid"
        placeholder="UUID of the story button should lead to (for example 6911589e-3567-43b5-ae8d-4a0ae3d7418c)"
        value={storyUuid}
        onChange={handleChange}
        error={error}
      />
      {matchedBookTitle ? (
        <div className="small mt-1 text-primary">{matchedBookTitle}</div>
      ) : null}
    </>
  );
}

export function ButtonTargetInput(props: ButtonTargetInputProps) {
  const {
    buttonAction,
    value,
    onChange,
    error,
  } = props;
  switch (buttonAction) {
    case 'story':
      return (
        <StoryBookUuidField value={value} onChange={onChange} error={error} />
      );
    case 'chat': {
      let channelId = '';
      try {
        channelId = JSON.parse(value).channelId;
      } catch {
        // TODO: report to sentry
      }
      const handleChange = (event: ChangeEvent<HTMLInputElement>) => {
        const newValue = JSON.stringify({
          channelId: event.target.value,
        });
        onChange(newValue);
      };
      return (
        <TextField
          label="Channel Id"
          name="channelId"
          placeholder="Channel id (ie announcements, new_releases, arcana_quest_info)"
          value={channelId}
          onChange={handleChange}
          error={error}
        />
      );
    }
    default:
      return null;
  }
}
